import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAllMacros, refreshMacros } from '../services/macroService';
import { useAuth } from './AuthContext';

const MacrosContext = createContext();

export const useMacros = () => {
  const context = useContext(MacrosContext);
  if (!context) {
    throw new Error('useMacros must be used within a MacrosProvider');
  }
  return context;
};

export const MacrosProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchMacros = useCallback(async (forceRefresh = false) => {
    // If we have macros and they're less than 10 minutes old, don't refetch
    if (!forceRefresh && macros.length > 0 && lastFetched && 
        Date.now() - lastFetched < 10 * 60 * 1000) {
      return macros;
    }

    setLoading(true);
    setError(null);
    
    try {
      const macroList = await getAllMacros();
      setMacros(macroList);
      setLastFetched(Date.now());
      return macroList;
    } catch (err) {
      // Don't set error for authentication issues - they're expected during page refresh
      if (err.message.includes('not authenticated') || err.message.includes('Authentication failed')) {
        console.log('Authentication not ready yet, skipping macro fetch');
        return [];
      }
      setError(err.message);
      console.error('Error fetching macros:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [lastFetched, macros]);

  const refreshMacrosData = useCallback(async () => {
    try {
      const macroList = await refreshMacros();
      setMacros(macroList);
      setLastFetched(Date.now());
      setError(null);
      return macroList;
    } catch (err) {
      // Don't set error for authentication issues - they're expected during page refresh
      if (err.message.includes('not authenticated') || err.message.includes('Authentication failed')) {
        console.log('Authentication not ready yet, skipping macro refresh');
        return [];
      }
      setError(err.message);
      console.error('Error refreshing macros:', err);
      throw err;
    }
  }, []);

  // Fetch macros on mount, but only when user is authenticated
  useEffect(() => {
    if (!authLoading && user) {
      fetchMacros();
    }
  }, [fetchMacros, authLoading, user]);

  const value = {
    macros,
    loading,
    error,
    lastFetched,
    fetchMacros,
    refreshMacros: refreshMacrosData,
    hasMacros: macros.length > 0
  };

  return (
    <MacrosContext.Provider value={value}>
      {children}
    </MacrosContext.Provider>
  );
};

export default MacrosContext;
